import { Stack, Typography } from '@mui/material';
import { TypographyProps } from '@mui/material/Typography';
import clsx from 'clsx';
import { X } from 'phosphor-react';
import { ArrayValues } from 'type-fest';

import { IconButton } from '../../../components/buttons';
import { tokens } from '../../../theme/generated/tokens';
import { SxProps } from '../../../types';
import { mergeSx } from '../../../utils';
import { classNamesFor } from '../../../utils/classNames';

export const inputChipSizes = ['s', 'm'] as const;
export type InputChipSize = ArrayValues<typeof inputChipSizes>;

export const inputChipStates = [
  'default',
  'disabled',
  'readOnly',
  'error',
] as const;
export type InputChipState = ArrayValues<typeof inputChipStates>;

const factory = classNamesFor('InputChip');

export const inputChipClassNames = {
  root: factory.root(),
  label: factory.element('label'),
  closeButton: factory.element('closeButton'),
  ...factory.toAttrClasses('size', inputChipSizes),
  ...factory.toAttrClasses('state', inputChipStates),
};

type SizeConfig = {
  height: number;
  labelProps: TypographyProps;
  iconSize: number;
};

const sizeConfigMap: Record<InputChipSize, SizeConfig> = {
  s: {
    height: 20,
    labelProps: { variant: 'P10M' },
    iconSize: 12,
  },
  m: {
    height: 24,
    labelProps: { variant: 'P12M' },
    iconSize: 12,
  },
};

type StateConfig = {
  bg: string;
  fg: string;
  border: string;
  hoverBg?: string;
};

const stateConfigMap: Record<InputChipState, StateConfig> = {
  default: {
    bg: tokens.theme.bg.structural.surfaceRaised,
    fg: tokens.theme.fg.structural.primary,
    border: tokens.theme.border.structural.neutralStrong,
  },
  disabled: {
    bg: tokens.theme.disabled.bg,
    fg: tokens.theme.disabled.fg,
    border: tokens.theme.disabled.border,
  },
  readOnly: {
    bg: tokens.theme.readonly.bg,
    fg: tokens.theme.readonly.fg,
    border: tokens.theme.border.structural.neutral,
  },
  error: {
    bg: tokens.theme.bg.status.errorSubtle,
    fg: tokens.theme.fg.structural.primary,
    border: tokens.theme.border.status.error,
  },
};

export type InputChipProps = {
  /** Chip label text — rendered in medium weight */
  label: string;
  /**
   * Optional value text rendered after the label in regular weight.
   * Use this to show "Label: value" with distinct typographic weights.
   */
  value?: string;
  /** Size variant */
  size?: InputChipSize;
  /** Visual state */
  state?: InputChipState;
  /** Called when the close button is clicked */
  onDelete?: () => void;
  /** Additional CSS class */
  className?: string;
  /** `data-testid` attribute value */
  testId?: string;
  /** MUI sx prop */
  sx?: SxProps;
};

export const InputChip = ({
  label,
  value,
  size = 'm',
  state = 'default',
  onDelete,
  className,
  testId,
  sx,
}: InputChipProps) => {
  const { height, labelProps, iconSize } = sizeConfigMap[size];
  const { bg, fg, border } = stateConfigMap[state];
  const isInteractive = state !== 'disabled' && state !== 'readOnly';
  const showClose = state !== 'readOnly';

  return (
    <Stack
      direction="row"
      alignItems="center"
      className={clsx(
        inputChipClassNames.root,
        inputChipClassNames[size],
        inputChipClassNames[state],
        className
      )}
      data-testid={testId}
      sx={mergeSx(
        {
          display: 'inline-flex',
          height,
          paddingLeft: '8px',
          paddingRight: showClose ? '4px' : '8px',
          gap: '4px',
          borderRadius: tokens.theme.radius.sm,
          backgroundColor: bg,
          color: fg,
          border: `1px solid ${border}`,
          flexShrink: 0,
          ...(isInteractive && {
            '&:hover': {
              backgroundColor: tokens.theme.border.action.neutralHover,
              borderColor: 'transparent',
            },
          }),
        },
        sx
      )}
    >
      <Typography
        component="span"
        className={inputChipClassNames.label}
        sx={{
          whiteSpace: 'nowrap',
          color: 'inherit',
        }}
        {...labelProps}
      >
        {label}
      </Typography>
      {value != null && (
        <Typography
          component="span"
          variant={size === 's' ? 'P10R' : 'P12R'}
          sx={{
            whiteSpace: 'nowrap',
            color: 'inherit',
          }}
        >
          {value}
        </Typography>
      )}
      {showClose && (
        <IconButton
          className={inputChipClassNames.closeButton}
          variant="ghost"
          onClick={state !== 'disabled' ? onDelete : undefined}
          disabled={state === 'disabled'}
          aria-label="Remove"
          sx={{
            padding: 0,
            width: iconSize,
            height: iconSize,
            minWidth: 0,
            '&:hover': {
              boxShadow: 'none',
              backgroundColor: tokens.primitives.colors.constants.transparent,
            },
          }}
        >
          <X size={iconSize} weight="bold" />
        </IconButton>
      )}
    </Stack>
  );
};
